import { FTUXType } from "../../Player/PlayerProperty/Progress/FTUXType";
import { report } from '../../report';

// WARNING: these values are also used as storage key
export enum BoosterType {
    JOKER = 'boosterJoker',
    UNDO = 'boosterUndo',
    TNT = 'boosterTNT',
    SHUFFLE = 'boosterShuffle',
    BULLS_EYE = 'boosterBullseye',
    PLUS_FIVE = 'boosterPlusFive',
    PLUS_THREE = 'boosterPlusThree',
    PLUS_ONE = 'boosterPlusOne',
}

export enum PreBoosterType {
    REMOVE_THREE = 'preBoosterRemoveThree',
    JOKER = 'preBoosterJoker',
    CANISTER = 'preBoosterCanister',
}

export type AnyBoosterType = BoosterType | PreBoosterType;

export const isPreBoosterType = (type: AnyBoosterType): type is PreBoosterType => {
    return Object.values(PreBoosterType).includes(type as PreBoosterType);
};

export function createBoostersRecord<T>(
    mapFunc: (boosterType: BoosterType) => T
): Record<BoosterType, T> {
    return {
        [BoosterType.JOKER]: mapFunc(BoosterType.JOKER),
        [BoosterType.UNDO]: mapFunc(BoosterType.UNDO),
        [BoosterType.TNT]: mapFunc(BoosterType.TNT),
        [BoosterType.SHUFFLE]: mapFunc(BoosterType.SHUFFLE),
        [BoosterType.BULLS_EYE]: mapFunc(BoosterType.BULLS_EYE),
        [BoosterType.PLUS_FIVE]: mapFunc(BoosterType.PLUS_FIVE),
        [BoosterType.PLUS_THREE]: mapFunc(BoosterType.PLUS_THREE),
        [BoosterType.PLUS_ONE]: mapFunc(BoosterType.PLUS_ONE),
    };
}

export function createPreBoostersRecord<T>(
    mapFunc: (preBoosterType: PreBoosterType) => T
): Record<PreBoosterType, T> {
    return {
        [PreBoosterType.REMOVE_THREE]: mapFunc(PreBoosterType.REMOVE_THREE),
        [PreBoosterType.JOKER]: mapFunc(PreBoosterType.JOKER),
        [PreBoosterType.CANISTER]: mapFunc(PreBoosterType.CANISTER),
    };
}

export function createAnyBoostersRecord<T>(
    mapFunc: (anyBoosterType: AnyBoosterType) => T
): Record<AnyBoosterType, T> {
    return {
        ...createBoostersRecord(mapFunc),
        ...createPreBoostersRecord(mapFunc),
    };
}

interface IBoosterSettings {
    localizationKey: string;
    texture: string;
    ftuxRequirement?: FTUXType;
}

const boosterDataMap: Record<AnyBoosterType, IBoosterSettings> = {
    [BoosterType.JOKER]: {
        localizationKey: 'Joker',
        texture: 'joker_reward',
        ftuxRequirement: FTUXType.JOKER,
    },
    [BoosterType.UNDO]: {
        localizationKey: 'Undo',
        texture: 'undo_reward',
        ftuxRequirement: FTUXType.UNDO,
    },
    [BoosterType.TNT]: {
        localizationKey: 'boosterTNT',
        texture: 'gas_can_reward',
        ftuxRequirement: FTUXType.TOOLBOX_UNLOCK,
    },
    [BoosterType.SHUFFLE]: {
        localizationKey: 'boosterShuffle',
        texture: 'shuffle_reward',
        ftuxRequirement: FTUXType.SHUFFLE_BOOSTER,
    },
    [BoosterType.BULLS_EYE]: {
        localizationKey: 'boosterBullseye',
        texture: 'bullseye-reward',
        ftuxRequirement: FTUXType.BULLSEYE_BOOSTER,
    },
    [BoosterType.PLUS_FIVE]: {
        localizationKey: 'Plus 5 Booster',
        texture: 'shop_5_cards',
    },
    [BoosterType.PLUS_ONE]: {
        localizationKey: 'Card',
        texture: 'extra_card_reward',
    },
    [BoosterType.PLUS_THREE]: null,

    // todo implement with preBoosters
    [PreBoosterType.REMOVE_THREE]: {
        localizationKey: 'RemoveThreePreBoosterShortName',
        texture: 'booster_remove',
    },
    [PreBoosterType.JOKER]: {
        localizationKey: 'JokerPreBoosterShortName',
        texture: 'booster_joker',
    },
    [PreBoosterType.CANISTER]: {
        localizationKey: 'CanisterPreBoosterShortName',
        texture: 'booster_canister',
    },
} as const;

export function getBoosterData(
    type: keyof typeof boosterDataMap
): IBoosterSettings {
    const data = boosterDataMap[type];
    if (!data) {
        report('Unable to find data for given booster type', {
            once: true,
            tags: { type },
            sampleRate: 0.1
        });
        return {
            localizationKey: 'localizationKey',
            texture: 'blank',
        };
    }
    return data;
}
